import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getInventoryLedger, getLedgerLookups } from "../../services/inventoryLedgerService";
import { ledgerEffectText, withStoreBalance } from "../../utils/itemLedgerBalance";
import { ledgerShortDate, searchLedger, withCurrentItem, withReferenceNo } from "../../utils/ledgerView";

const CHIPS = [
  { key: "all",   label: "All" },
  { key: "store", label: "Store" },
  { key: "site",  label: "Site" },
];

const TH   = "sticky top-0 z-10 bg-slate-800 text-white text-left font-semibold px-2 py-2";
const CELL = "px-2 py-1.5 border-b align-top";

export default function InventoryLedger() {

  const [ledger,  setLedger]  = useState([]);
  const [lookups, setLookups] = useState({ items: [], poNumbers: new Map() });
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [kind,    setKind]    = useState("all");
  const navigate = useNavigate();

  useEffect(() => { fetchLedger(); }, []);

  const fetchLedger = async () => {
    try {
      const user      = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user.company_id || "";
      if (!companyId) { alert("Company ID not found. Please log in again."); return; }
      const [data, found] = await Promise.all([
        getInventoryLedger(companyId),
        getLedgerLookups(companyId),
      ]);
      setLedger(data);
      setLookups(found);
    } catch (error) {
      console.error("Ledger fetch failed:", error);
      alert("Failed to load inventory ledger");
    } finally {
      setLoading(false);
    }
  };

  // ── RUNNING BALANCE — store stock only, per item (P4-5c) ────────────
  // On the FULL list, before any search: a filtered row keeps its true balance.
  const rows = useMemo(() => withReferenceNo(
    withCurrentItem(withStoreBalance(ledger), lookups.items),
    lookups.poNumbers
  ), [ledger, lookups]);

  const shown = useMemo(() => searchLedger(rows, { text: search, kind }), [rows, search, kind]);

  const openItem = (itemId) => navigate(`/item-ledger/${encodeURIComponent(itemId)}`);

  if (loading) return (
    <div className="p-6 text-lg font-medium">Loading Inventory Ledger...</div>
  );

  return (
    <div className="min-h-screen bg-slate-100 p-2">
      <div className="w-full bg-white rounded-2xl shadow-lg border p-4">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-2xl font-bold text-gray-800">Inventory Ledger</h2>
          <div className="text-sm text-gray-500">
            Showing{" "}
            <span className="font-semibold text-black">{shown.length}</span>
            {" "}of{" "}
            <span className="font-semibold text-black">{rows.length}</span>
          </div>
        </div>

        {/* SEARCH + CHIPS */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item name, code, item id or reference no."
            className="flex-1 min-w-[240px] border rounded-lg px-3 py-2 text-sm"
          />
          {CHIPS.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => setKind(chip.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold ${
                kind === chip.key
                  ? "bg-slate-800 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}>
              {chip.label}
            </button>
          ))}
        </div>

        {/* TABLE — its own scroll box, so the header stays in view */}
        <div className="overflow-auto rounded-xl border max-h-[calc(100vh-13rem)]">
          <table className="w-full min-w-[960px] table-fixed border-separate border-spacing-0 text-sm">

            <thead>
              <tr>
                <th className={`${TH} w-[92px]`}>Date</th>
                <th className={`${TH} w-[240px]`}>Item</th>
                <th className={`${TH} w-[150px]`}>Movement</th>
                <th className={`${TH} w-[80px]`}>Qty</th>
                <th className={`${TH} w-[64px]`}>Effect</th>
                <th className={`${TH} w-[72px]`}>Balance</th>
                <th className={`${TH} w-[170px]`}>Site / Project</th>
                <th className={`${TH} w-[178px]`}>Reference</th>
              </tr>
            </thead>

            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center p-8 text-gray-500">
                    {rows.length === 0 ? "No inventory records found" : "No entries match this search"}
                  </td>
                </tr>
              ) : (
                shown.map((entry, index) => (
                  <tr key={entry.id || index}
                    onClick={() => { if (entry.item_id) openItem(entry.item_id); }}
                    className={`transition-all hover:bg-cyan-50 ${
                      entry.item_id ? "cursor-pointer" : ""
                    } ${index % 2 === 0 ? "bg-slate-50" : "bg-white"}`}>

                    {/* DATE */}
                    <td className={`${CELL} text-xs text-gray-600`}>
                      {ledgerShortDate(entry.created_at)}
                    </td>

                    {/* ITEM — name, current code, old code if it changed */}
                    <td className={CELL}>
                      <button
                        type="button"
                        disabled={!entry.item_id}
                        title={entry.item_name || entry.master_name || ""}
                        onClick={(e) => { e.stopPropagation(); openItem(entry.item_id); }}
                        className="block max-w-full truncate text-left font-semibold text-cyan-700 hover:text-cyan-900 hover:underline">
                        {entry.item_name || entry.master_name || "-"}
                      </button>
                      <div className="text-xs text-gray-500 truncate"
                        title={entry.old_code ? `was ${entry.old_code}` : undefined}>
                        {entry.current_code || "-"}
                        {entry.old_code ? ` · was ${entry.old_code}` : ""}
                      </div>
                    </td>

                    {/* MOVEMENT TYPE */}
                    <td className={CELL}>
                      <span className={`inline-block max-w-full break-all px-2 py-0.5 rounded-lg text-[11px] font-bold ${
                        entry.stock_effect > 0
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}>
                        {entry.movement_type || "-"}
                      </span>
                      {entry.ledger_kind === "site" && (
                        <span className="ml-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-200 text-slate-600">
                          Site
                        </span>
                      )}
                    </td>

                    {/* QTY + UNIT */}
                    <td className={`${CELL} font-medium`}>
                      {entry.qty || 0}{" "}
                      <span className="text-xs font-normal text-gray-500">{entry.unit || ""}</span>
                    </td>

                    {/* STOCK EFFECT */}
                    <td className={`${CELL} font-bold ${
                      entry.ledger_kind !== "store"
                        ? "text-gray-400"
                        : entry.stock_effect > 0 ? "text-green-600" : "text-red-600"
                    }`}>
                      {ledgerEffectText(entry)}
                    </td>

                    {/* RUNNING BALANCE */}
                    <td className={`${CELL} font-semibold text-slate-800`}>
                      {entry.store_balance}
                    </td>

                    {/* SITE / PROJECT */}
                    <td className={CELL}>
                      <div className="truncate" title={entry.site_name || entry.site_id || undefined}>
                        {entry.site_name || entry.site_id || "-"}
                      </div>
                      <div className="text-xs text-gray-500 truncate"
                        title={entry.project_name || entry.project_id || undefined}>
                        {entry.project_name || entry.project_id || "-"}
                      </div>
                    </td>

                    {/* REFERENCE — remarks in full on hover */}
                    <td className={CELL} title={entry.remarks || undefined}>
                      <div className="text-xs font-medium truncate">{entry.reference_type || "-"}</div>
                      <div className="text-xs text-gray-600 truncate">{entry.reference_no || "-"}</div>
                      {entry.remarks && (
                        <div className="text-xs text-gray-400 truncate">{entry.remarks}</div>
                      )}
                    </td>

                  </tr>
                ))
              )}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  );
}
